import React from 'react';
import type { GearItem } from '@/types/GearConfig';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GearItemRowProps {
  item: GearItem;
  onChange: (updated: GearItem) => void;
  onDelete: () => void;
}

export const GearItemRow: React.FC<GearItemRowProps> = ({
  item,
  onChange,
  onDelete,
}) => {
  return (
    <div className="flex flex-col gap-3 p-3 rounded border bg-surface-1">
      {/* Row header: name + delete */}
      <div className="flex items-center justify-between gap-2">
        <input
          type="text"
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="Item name"
          className="h-8 flex-1 rounded border bg-surface-2 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Rate inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground w-12 shrink-0">Daily</Label>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">$</span>
            <input
              type="number"
              value={item.daily_rate || ''}
              onChange={(e) => onChange({ ...item, daily_rate: e.target.value ? parseFloat(e.target.value) : 0 })}
              placeholder="0.00"
              className={cn(
                "h-8 w-full rounded border bg-surface-2 px-2 text-right text-xs font-mono",
                "focus:outline-none focus:ring-1 focus:ring-primary"
              )}
              step={0.01}
              min={0}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground w-14 shrink-0">Weekly</Label>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">$</span>
            <input
              type="number"
              value={item.weekly_rate ?? ''}
              onChange={(e) => onChange({ ...item, weekly_rate: e.target.value ? parseFloat(e.target.value) : undefined })}
              placeholder="Optional"
              className={cn(
                "h-8 w-full rounded border bg-surface-2 px-2 text-right text-xs font-mono",
                "focus:outline-none focus:ring-1 focus:ring-primary"
              )}
              step={0.01}
              min={0}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
